import React, { useState, useEffect, useCallback } from "react";
import { BASE_URL } from "../../config/api";
import { useLocation, useNavigate } from "react-router-dom";

export default function OrderDetails() {
  const { state } = useLocation();
  const navigate = useNavigate();

  const [order, setOrder] = useState(null);
  const [loading, setLoading] = useState(true);

  // ✅ SAFE ACCESS
  const orderno = state?.orderno;

  // ✅ FETCH ORDER DETAILS
  const fetchOrderDetails = useCallback(() => {
    if (!orderno) return;

    // console.log("Fetching order:", orderno); // DEBUG

    setLoading(true);

    fetch(
      `https://lifetimedesign.in/LtcJsonAPI.asmx/OrderDetails?orderno=${orderno}`,
    )
      .then((res) => res.json())
      .then((data) => {
        // console.log("API RESPONSE:", data); // DEBUG

        if (data?.success && data.data) {
          setOrder({
            info: data.data.order?.[0],
            items: data.data.detail || [],
          });
        } else {
          setOrder(null);
        }
      })
      .catch((err) => {
        console.error("Fetch error:", err);
        setOrder(null);
      })
      .finally(() => setLoading(false));
  }, [orderno]);

  useEffect(() => {
    fetchOrderDetails();
  }, [fetchOrderDetails]);

  // ✅ STATES
  if (!orderno) {
    return <div className="text-center mt-5">❌ Order number missing</div>;
  }

  if (loading) {
    return <div className="text-center mt-5">⏳ Loading order details...</div>;
  }

  if (!order) {
    return <div className="text-center mt-5">❌ Order not found</div>;
  }

  const isConfirmed = order.info?.isconfirmed === 1;
  const totalDesigns = order.items?.length || 0;

  const totalAmount = order.items?.reduce(
    (sum, item) => sum + Number(item.amount || 0),
    0,
  );
  const orderDateTime =
    order.info?.orderdate || order.info?.created_at || order.info?.date || "";

  // Adjust field name based on your API (try orderdate / created_at etc.)
  const orderDate = order.info?.orderdate;
  const breadcrumbItems = [
    {
      label: (
        <>
          <i className="bi bi-house"></i> Home
        </>
      ),
      link: "/",
    },
    {
      label: "My Orders",
      link: "/myorders",
    },
    {
      label: "Order Details",
      link: "/order-details",
    },
  ];
  return (
    <div className="wish-page" style={{ paddingTop: "20px" }}>
      {/* HEADER */}
      <nav aria-label="breadcrumb">
        <ol className="breadcrumb">
          {breadcrumbItems.map((item, i) => (
            <li
              key={i}
              className={`breadcrumb-item ${i === breadcrumbItems.length - 1 ? "active" : ""}`}
            >
              {i === breadcrumbItems.length - 1 ? (
                item.label
              ) : (
                <a href={item.link}>{item.label}</a>
              )}
            </li>
          ))}
        </ol>
      </nav>

      <div className="card mb-4 p-3 shadow-sm border-0">
        <div className="d-flex justify-content-between mb-4">
          <h4 className="fw-bold mb-3">Order Information</h4>
          <button
            className="btn btn-outline-secondary btn-sm"
            onClick={() => navigate("/myorders")}
          >
            ← Back
          </button>
        </div>

        <div className="row">
          <div className="col-md-3 mb-2">
            <h5>Order No: {orderno}</h5>
          </div>

          <div className="col-md-3 mb-2">
            <h5>Total Designs: {totalDesigns}</h5>
          </div>

          <div className="col-md-3 mb-2">
            <h5>Total Amount: ₹{totalAmount}</h5>
          </div>

          <div className="col-md-3 mb-2">
            <h5>Order Date: {order.info?.orderdate || "-"} </h5>
          </div>
        </div>
      </div>
      {/* TABLE */}
      <div className="table-responsive">
        <table className="table table-bordered table-striped align-middle">
          <thead className="table-dark1">
            <tr>
              <th className="text-center">#</th>
              <th className="text-center">Image</th>
              <th>Design Info</th>

              <th className="text-center">Amount</th>
              <th className="text-center">Status</th>
              <th className="text-center">Download</th>
            </tr>
          </thead>

          <tbody className="table-body">
            {order.items?.map((item, index) => {
              const fileName = item.emb_file?.endsWith(".txt")
                ? item.emb_file.replace(".txt", ".emb")
                : item.emb_file;

              return (
                <tr key={index}>
                  <td className="text-center">{index + 1}</td>

                  <td className="text-center">
                    <img
                      src={`https://lifetimedesign.in/controlpanel/Designs/${item.image_url}`}
                      alt=""
                      style={{
                        width: "100px",
                        height: "150px",
                        objectFit: "contain",
                      }}
                    />
                  </td>

                  <td>
                    <div className="design-info">
                      <div>
                        <strong>Code:</strong> {item.code}
                      </div>
                      <div>
                        <strong>Stitches:</strong> {item.stiches}
                      </div>
                      <div>
                        <strong>Area:</strong> {item.area}
                      </div>
                      <div>
                        <strong>Color:</strong> {item.color}
                      </div>
                      <div>
                        <strong>Concept:</strong> {item.concept}
                      </div>
                      <div>
                        <strong>Height:</strong> {item.packageno}
                      </div>
                      <div>
                        <strong>Width:</strong> {item.sequence}
                      </div>
                      <div>
                        <strong>Details:</strong> {item.description}
                      </div>
                    </div>
                  </td>

                  <td className="fw-bold text-primary text-center">
                    ₹{item.amount}
                  </td>

                  <td className="text-center">
                    {isConfirmed ? (
                      <span className="text-success fs-4">
                        <i className="bi bi-check-circle-fill"></i>
                      </span>
                    ) : (
                      <span className="text-danger fs-4">
                        <i className="bi bi-x-circle-fill"></i>
                      </span>
                    )}
                  </td>

                  <td className="text-center">
                    {isConfirmed && fileName ? (
                      <a
                        href={`${BASE_URL}/download.php?file=${encodeURIComponent(item.emb_file)}`}
                        className="btn btn-success btn-sm"
                      >
                        Download
                      </a>
                    ) : (
                      <span className="text-muted small"></span>
                    )}
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>
    </div>
  );
}
